import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Card, 
  Descriptions, 
  Tag, 
  Button, 
  Spin, 
  Empty,
  message,
  Modal, 
  Row, 
  Col,
  Progress,
  Divider,
  Space
} from 'antd';
import { 
  LeftOutlined, 
  EditOutlined, 
  DeleteOutlined,
  SmileOutlined,
  FrownOutlined,
  MehOutlined,
  RobotOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { emotionRecordApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDate } from '../utils/dateUtils';

const { Title, Text, Paragraph } = Typography;
const { confirm } = Modal;

const EmotionRecordDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState(null);

  useEffect(() => {
    const fetchRecord = async () => {
      setLoading(true);
      try {
        const response = await emotionRecordApi.getEmotionRecord(id);
        console.log("获取的情绪记录详情:", response.data);
        
        // 确保情绪对象存在，如果不存在则创建默认对象
        const recordData = {
          ...response.data,
          emotion: response.data.emotion || { 
            name: '未知情绪', 
            intensity: response.data.intensity || 5,
            description: '无情绪描述'
          }
        };
        
        setRecord(recordData);
      } catch (error) {
        console.error('获取情绪记录详情失败:', error);
        message.error('获取情绪记录详情失败');
        navigate('/emotion-records');
      } finally {
        setLoading(false);
      }
    };
    
    fetchRecord();
  }, [id, navigate]);

  // 确认删除
  const showDeleteConfirm = () => {
    confirm({
      title: '确定要删除这条情绪记录吗?',
      content: '删除后无法恢复',
      okText: '确定',
      okType: 'danger',
      cancelText: '取消',
      onOk: handleDelete
    });
  };

  // 处理删除
  const handleDelete = async () => {
    try {
      await emotionRecordApi.deleteEmotionRecord(id);
      message.success('情绪记录已删除');
      navigate('/emotion-records');
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除情绪记录失败');
    }
  };

  // 获取情绪图标
  const getEmotionIcon = (intensity) => {
    if (!intensity && intensity !== 0) return <MehOutlined />;
    if (intensity > 5) return <SmileOutlined />;
    if (intensity < 5) return <FrownOutlined />;
    return <MehOutlined />;
  };

  // 获取情绪标签颜色
  const getEmotionColor = (intensity) => {
    if (!intensity && intensity !== 0) return '';
    if (intensity > 7) return 'success';
    if (intensity > 5) return 'processing';
    if (intensity < 3) return 'error';
    if (intensity < 5) return 'warning';
    return 'default';
  };

  // 获取情绪强度的进度条颜色
  const getIntensityColor = (intensity) => {
    if (!intensity && intensity !== 0) return '#1890ff';
    if (intensity > 7) return '#52c41a';
    if (intensity > 5) return '#1890ff';
    if (intensity < 3) return '#f5222d';
    if (intensity < 5) return '#faad14';
    return '#1890ff';
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '20px' }}>加载情绪记录详情...</div>
      </div>
    );
  }

  if (!record) {
    return (
      <div className="container">
        <Empty 
          description="未找到情绪记录" 
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <Button type="primary" onClick={() => navigate('/emotion-records')}>
            返回列表
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ marginBottom: '20px' }}>
        <Button 
          type="link" 
          icon={<LeftOutlined />} 
          onClick={() => navigate('/emotion-records')}
          style={{ padding: 0 }}
        >
          返回列表
        </Button>
      </div>
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <Title level={2} style={{ marginBottom: '8px' }}>
            情绪记录详情
          </Title>
          <Text type="secondary">
            <CalendarOutlined style={{ marginRight: '8px' }} />
            {formatDate(record.createdAt)}
          </Text>
        </div>
        <Space>
          <Button 
            icon={<EditOutlined />} 
            onClick={() => navigate(`/emotion-records/${id}/edit`)}
          >
            编辑
          </Button>
          <Button 
            danger 
            icon={<DeleteOutlined />} 
            onClick={showDeleteConfirm}
          >
            删除
          </Button>
        </Space>
      </div>
      
      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Card title="情绪信息" style={{ marginBottom: '16px' }}>
            <div style={{ textAlign: 'center', margin: '20px 0' }}>
              {record.emotion && (
                <Tag 
                  color={getEmotionColor(record.emotion.intensity)} 
                  icon={getEmotionIcon(record.emotion.intensity)}
                  style={{ padding: '8px 16px', fontSize: '16px' }}
                >
                  {record.emotion.name}
                </Tag>
              )}
            </div>
            
            <div style={{ textAlign: 'center', margin: '20px 0' }}>
              <div style={{ marginBottom: '8px' }}>情绪强度</div>
              <Progress
                type="dashboard"
                percent={(record.intensity || 5) * 10}
                format={() => record.intensity || 5}
                strokeColor={getIntensityColor(record.intensity)}
              />
            </div>
            
            {record.emotion && record.emotion.description && (
              <div style={{ marginTop: '20px' }}>
                <Paragraph>
                  <strong>情绪描述：</strong>
                </Paragraph>
                <Paragraph>{record.emotion.description}</Paragraph>
              </div>
            )}
          </Card>
        </Col>
        
        <Col xs={24} md={16}>
          <Card style={{ marginBottom: '16px' }}>
            <Descriptions
              title="事件详情"
              bordered
              column={{ xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
            >
              <Descriptions.Item label="触发事件">
                {record.event || record.description || '未记录'}
              </Descriptions.Item>
              <Descriptions.Item label="身体反应">
                {record.physicalResponse || '未记录'}
              </Descriptions.Item>
              <Descriptions.Item label="想法和信念">
                {record.thoughtsAndBeliefs || '未记录'}
              </Descriptions.Item>
              <Descriptions.Item label="行为反应">
                {record.behaviors || '未记录'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
          
          {record.gptAnalysis && (
            <Card 
              title={
                <span>
                  <RobotOutlined style={{ marginRight: '8px' }} />
                  AI 分析结果
                </span>
              }
            >
              <Paragraph>{record.gptAnalysis}</Paragraph>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default EmotionRecordDetailPage; 